/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import { createSlice } from '@reduxjs/toolkit';
import { idField } from '@constants';

const initialState = {
  all: [],
  current: {},
  filtered: {
    domain: '',
    intents: undefined,
  },
  content: {
    sentences: [],
    tests: [],
    answers: [],
    tags: [],
    toEdit: {
      sentence: '',
      test: '',
      answer: '',
      tags: [],
    },
  },
  loading: false,
  searchText: '',
  deepSearch: '',
  qualityBundle: undefined,
  forkedIntents: {
    versionId: undefined,
    duplicated: [],
    applyAll: undefined,
  },
};

const intentsSlice = createSlice({
  name: 'intents',
  initialState,
  reducers: {
    editableIntentContent: {
      reducer(state, action) {
        state.content.toEdit = { ...action.payload };
      },
    },
    resetAll: {
      reducer(state) {
        Object.assign(state, initialState);
      },
    },
    addContent: {
      reducer(state, action) {
        const { contentType, content } = action.payload;
        state.content[contentType].push(content);
      },
    },
    deleteContent: {
      reducer(state, action) {
        const filterContent = (arr, index) => arr.filter((el, i) => i !== index && el);
        const { contentType, index } = action.payload;
        if (contentType === 'answers') {
          state.content.tags = filterContent(state.content.tags, index);
        }
        state.content[contentType] = filterContent(state.content[contentType], index);
      },
    },
    filteredByDomain: {
      reducer(state, action) {
        const { domainId } = action.payload;
        // TODO Pending to filter intents by domainId after CRUD actions are created.
        const filteredIntents = state.all.filter(intents => intents[idField] === domainId);
        state.filtered.domain = domainId;
        state.filtered.intents.push(filteredIntents);
      },
    },
    clearEditions: {
      reducer(state) {
        state.content.toEdit = initialState.content.toEdit;
      },
    },
    clearContent: {
      reducer(state) {
        state.content = initialState.content;
      },
    },
    intentsRequest: {
      reducer(state) {
        state.loading = true;
      },
    },
    intentsFailure: {
      reducer(state) {
        state.loading = false;
      },
    },
    addIntentSuccess: {
      reducer(state, action) {
        state.all.push(action.payload);
        state.current = { ...action.payload };
        state.loading = false;
      },
    },
    getIntentsSuccess: {
      reducer(state, action) {
        state.all = [...action.payload];
        state.current = {};
        state.loading = false;
      },
    },
    getIntentSuccess: {
      reducer(state, action) {
        state.current = { ...action.payload };
        state.loading = false;
      },
    },
    updateIntentSuccess: {
      reducer(state, action) {
        return {
          ...state,
          all: state.all.map(intent => (intent[idField] !== action.payload[idField] ? intent : action.payload)),
          loading: false,
        };
      },
    },
    deleteIntentSuccess: {
      reducer(state, action) {
        const intents = state.all.filter(intent => intent[idField] !== action.payload);
        state.all = intents;
        state.loading = false;
      },
    },
    filterIntentsSuccess: {
      reducer(state, action) {
        const { searchText, deepSearch } = action.payload;
        state.searchText = searchText;
        state.deepSearch = deepSearch;
      },
    },
    deleteDomainIntents: {
      reducer(state, action) {
        const intents = state.all.filter(intent => intent.domainId !== action.payload);
        state.all = intents;
      },
    },
    setIntent: {
      reducer(state, action) {
        const itemSelected = state.all.filter(item => item[idField] === action.payload);
        const [item] = itemSelected;
        state.current = item;
      },
    },
    computeQualitySuccess: {
      reducer(state, action) {
        state.qualityBundle = action.payload;
        state.loading = false;
      },
    },
    setDuplicatedIntents: {
      reducer(state, action) {
        state.forkedIntents.duplicated = action.payload;
      },
    },
    setVersionForked: {
      reducer(state, action) {
        state.forkedIntents.versionId = action.payload;
      },
    },
    resetForkInformation: {
      reducer(state) {
        state.forkedIntents = initialState.forkedIntents;
      },
    },
    setApplyAllSelector: {
      reducer(state, action) {
        state.forkedIntents.applyAll = action.payload;
      },
    },
  },
});

export const {
  editableIntentContent,
  addContent,
  deleteContent,
  filteredByDomain,
  clearEditions,
  clearContent,
  intentsRequest,
  intentsFailure,
  addIntentSuccess,
  getIntentsSuccess,
  getIntentSuccess,
  updateIntentSuccess,
  deleteIntentSuccess,
  filterIntentsSuccess,
  deleteDomainIntents,
  setIntent,
  resetAll,
  computeQualitySuccess,
  setDuplicatedIntents,
  setVersionForked,
  resetForkInformation,
  setApplyAllSelector,
} = intentsSlice.actions;

export default intentsSlice.reducer;
